let shoppingCart;

// Returns "today" as a Date at local midnight, sourced from the SERVER clock
// (#server-now[data-today]="Y-m-d") so a visitor's wrong device clock can never
// shift the minimum pickup date. Falls back to the device clock if absent.
function getTodayMidnight() {
  const el = document.getElementById("server-now");
  const raw = el && el.dataset.today;
  if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }
  const fallback = new Date();
  fallback.setHours(0, 0, 0, 0);
  return fallback;
}

// Minimum pickup date = server today + N days (default 2), at local midnight.
const MIN_LEAD_DAYS = 2;
function getMinPickupDate(leadDays = MIN_LEAD_DAYS) {
  const min = getTodayMidnight();
  min.setDate(min.getDate() + leadDays);
  return min;
}

// Final client-side gate before submit: every pickup date must be present and
// no earlier than the server-based minimum. Belt-and-suspenders on top of the
// flatpickr disable — catches empty/stale values and any edge path. The server
// module is the real guarantee; this is for UX (tell the user before they POST).
function validatePickupDatesBeforeSubmit() {
  const dateInputs = document.querySelectorAll(
    'input[name="ophaaldatum"], input[name^="ophaaldatum-"]'
  );
  if (!dateInputs.length) return true; // not the order form

  const min = getMinPickupDate();
  const minLabel = min.toLocaleDateString("nl-BE");
  let firstBad = null;

  dateInputs.forEach((input) => {
    const value = (input.value || "").trim();
    if (!value) {
      firstBad = firstBad || { input, reason: "empty" };
      return;
    }
    const [d, m, y] = value.split("-").map(Number); // flatpickr: d-m-Y
    const picked = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
    if (isNaN(picked.getTime()) || picked < min) {
      firstBad = firstBad || { input, reason: "tooSoon" };
    }
  });

  if (firstBad) {
    const box =
      firstBad.input.closest(".freeform-row")?.querySelector(".pickup-message") ||
      document.getElementById("pickup-message");
    if (box) {
      box.textContent =
        firstBad.reason === "empty"
          ? "Kies een ophaaldatum."
          : `Bestellingen moeten minstens 2 dagen op voorhand geplaatst worden. De vroegst mogelijke ophaaldatum is ${minLabel}.`;
      box.classList.remove("hidden");
      scrollToIfHidden(box);
    }
    return false;
  }
  return true;
}

class ShoppingCart {
  constructor() {
    this.cart = JSON.parse(localStorage.getItem("cart")) || {
      items: [],
      subtotal: 0,
    };
    this.sanitizeCart(); // heal stale pickup dates / NaN subtotal left by older carts
    this.initializeEventListeners();
    this.renderCartContents(); // Render cart on page load
    this.pendingFilterOptions = null;
  }

  // Carts persist in localStorage across deploys, so items added weeks ago can
  // carry pickup dates that are now in the past (or before today + lead time),
  // and carts from an older schema can leave subtotal as "NaN". Repair both on
  // load so the order form always starts from a valid, current state.
  sanitizeCart() {
    if (!this.cart || !Array.isArray(this.cart.items)) {
      this.cart = { items: [], subtotal: 0 };
      return;
    }

    const min = getMinPickupDate();
    let changed = false;

    this.cart.items.forEach((item) => {
      const stored = item.pickupDate ? new Date(item.pickupDate) : null;
      if (!stored || Number.isNaN(stored.getTime()) || stored < min) {
        item.pickupDate = this.getNextAvailablePickupDate({
          isEveryDay: item.isEveryDay,
          days: Array.isArray(item.days) ? item.days : [],
        }).toISOString();
        changed = true;
      }
    });

    const before = this.cart.subtotal;
    this.calculateSubtotal();
    if (changed || this.cart.subtotal !== before) this.saveCart();
  }
  handleOutsideClick(event) {
    const modal = document.getElementById("cart-modal");
    if (!modal) return;

    // If modal is hidden, do nothing
    if (modal.classList.contains("hidden")) return;

    const modalContent = modal.querySelector(".modal-content");
    if (!modalContent) return;

    // Check if click was outside the modal content
    const isClickOutside = !modalContent.contains(event.target);

    // Check if click was on the toggle button (to prevent immediate closing when opening)
    const isToggleButton = event.target.closest(".hide-toggle");

    if (isClickOutside && !isToggleButton) {
      this.closeCart();
    }
  }

  getProductAvailability(sourceButton = null) {
    if (sourceButton) {
      const buttonIsEveryDay = sourceButton.dataset.isEveryDay;
      const buttonAvailableDays = sourceButton.dataset.availableDays;

      if (buttonIsEveryDay !== undefined || buttonAvailableDays !== undefined) {
        const isEveryDay = buttonIsEveryDay === "true";
        const availableDays = (buttonAvailableDays || "")
          .split(",")
          .map((day) => day.trim())
          .filter((day) => day && day !== "elkeDag");

        return { isEveryDay, days: availableDays };
      }
    }

    const availabilityData = document.getElementById(
      "product-availability-data"
    );
    if (!availabilityData) return { isEveryDay: true, days: [] };

    const isEveryDay = availabilityData.dataset.isEveryDay === "true";
    const availableDays = availabilityData.dataset.availableDays
      .split(",")
      .map((day) => day.trim())
      .filter((day) => day && day !== "elkeDag");

    return { isEveryDay, days: availableDays };
  }

  getNextAvailablePickupDate(availableDays) {
    // If available every day, pickup is always server date + 2 days
    if (availableDays.isEveryDay) {
      return getMinPickupDate();
    }

    // Map day values to day indices (0 = Sunday, 1 = Monday, etc.)
    const dayMapping = {
      maandag: 1,
      dinsdag: 2,
      woensdag: 3,
      donderdag: 4,
      vrijdag: 5,
      zaterdag: 6,
      zondag: 0,
    };

    // Convert available days to day indices
    const availableDayIndices = availableDays.days.map(
      (day) => dayMapping[day]
    );

    // Get current date and day of week (from the server clock)
    const today = getTodayMidnight();
    const currentDayIndex = today.getDay();

    // Find the next available day that's at least 2 days from now
    let daysToAdd = 2; // Minimum 2 days from now
    let nextAvailableDayFound = false;

    while (!nextAvailableDayFound) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + daysToAdd);
      const targetDayIndex = targetDate.getDay();

      if (availableDayIndices.includes(targetDayIndex)) {
        nextAvailableDayFound = true;
        const result = new Date(targetDate);
        return result;
      }

      daysToAdd++;

      // Safety check to prevent infinite loop
      if (daysToAdd > 14) break;
    }

    // Fallback: return date 2 days from now (server clock)
    return getMinPickupDate();
  }

  formatDate(date) {
    const options = { weekday: "long", day: "numeric", month: "long" };
    return date.toLocaleDateString("nl-NL", options);
  }

  initializeEventListeners() {
    // Add to cart buttons
    this.bindAddToCartButtons();
    this.setupFilterModal();
    // Cart toggle
    const cartToggle = document.querySelector(".hide-toggle");
    if (cartToggle) {
      cartToggle.setAttribute("aria-expanded", "false");
      cartToggle.addEventListener("click", () => this.toggleModal());
    }
    // Delete storage button
    const deleteStorage = document.querySelector(".delStorage");
    if (deleteStorage) {
      deleteStorage.addEventListener("click", () => this.delLocalStorage());
    }
    document.addEventListener("click", (e) => this.handleOutsideClick(e));
    // Initial cart count update
    this.updateCartCount();
    this.renderOrderSide();
  }
  bindAddToCartButtons(root = document) {
    root.querySelectorAll(".addBasket").forEach((button) => {
      if (button.dataset.cartBound === "true") return;
      button.addEventListener("click", (e) => this.handleAddButtonClick(e));
      button.dataset.cartBound = "true";
    });
    this.setupFilterModal();
  }

  setupFilterModal() {
    if (this.filterModalSetup) {
      return;
    }

    this.filterModal = document.getElementById("product-filter-modal");
    if (!this.filterModal) {
      return;
    }

    this.filterModalOptions = this.filterModal.querySelector(
      "[data-filter-modal-options]"
    );
    this.filterModalTitle = this.filterModal.querySelector(
      "[data-filter-modal-title]"
    );
    this.filterModalSubtitle = this.filterModal.querySelector(
      "[data-filter-modal-subtitle]"
    );

    this.filterModalSetup = true;
    this.pendingFilterButton = null;
    this.pendingFilterOptions = null;
  }

  getFilterOptionsFromButton(button) {
    const raw = button.dataset.filterOptions || "";
    if (!raw) return [];
    return raw
      .split("||")
      .map((option) => option.trim())
      .filter((option) => option.length > 0);
  }

  handleAddButtonClick(event) {
    const button = event.currentTarget;
    const filterOptions = this.getFilterOptionsFromButton(button);
    const fromDetail = button.dataset.variantSource === "detail";
    const detailChoice =
      button.dataset.selectedVariant || button.dataset.defaultVariant;
    const normalizedDetailChoice = this.normalizeFilterChoice(detailChoice);

    if (filterOptions.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      if (fromDetail && normalizedDetailChoice) {
        this.addItemFromButton(button, {
          filterChoice: normalizedDetailChoice,
          filterChoiceDisplay: detailChoice,
          filterOptions,
        });
      } else {
        this.openFilterModal(button, filterOptions);
      }
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.addItemFromButton(button, { filterOptions });
  }

  openFilterModal(button, options) {
    this.setupFilterModal();

    if (!this.filterModal || !this.filterModalOptions) {
      this.addItemFromButton(button, { filterOptions: options });
      return;
    }

    this.pendingFilterButton = button;
    this.pendingFilterOptions = Array.isArray(options) ? [...options] : [];

    const productTitle = button.dataset.productTitle || "dit product";
    const safeTitle = this.escapeHtml(productTitle);
    if (this.filterModalTitle) {
      this.filterModalTitle.textContent = "Maak een keuze";
    }
    if (this.filterModalSubtitle) {
      this.filterModalSubtitle.innerHTML = `Welke variant wil je bestellen voor <strong>${safeTitle}</strong>?`;
    }

    this.filterModalOptions.innerHTML = "";
    options.forEach((option) => {
      const optionButton = document.createElement("button");
      optionButton.type = "button";
      optionButton.className =
        "px-4 py-3 text-center rounded-lg border border-main text-main font-medium hover:bg-main hover:text-white transition duration-300 focus:outline-none focus:ring-2 focus:ring-main/40";
      optionButton.textContent = option;
      optionButton.addEventListener("click", () => {
        this.handleFilterChoice(option);
      });
      this.filterModalOptions.appendChild(optionButton);
    });

    this.filterModal.classList.remove("hidden");
    this.filterModal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("overflow-hidden");

    const firstOption = this.filterModalOptions.querySelector("button");
    if (firstOption) {
      firstOption.focus();
    }
  }

  handleFilterChoice(option) {
    if (!this.pendingFilterButton) {
      return;
    }

    const button = this.pendingFilterButton;
    const filterOptions = this.pendingFilterOptions || [];
    this.addItemFromButton(button, {
      filterChoice: option,
      filterChoiceDisplay: option,
      filterOptions,
    });
    this.closeFilterModal();
  }

  closeFilterModal() {
    if (!this.filterModal) {
      return;
    }

    this.filterModal.classList.add("hidden");
    this.filterModal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("overflow-hidden");
    if (this.filterModalOptions) {
      this.filterModalOptions.innerHTML = "";
    }
    this.pendingFilterButton = null;
    this.pendingFilterOptions = null;
  }

  addItemFromButton(button, overrides = {}) {
    const productId = button.dataset.productId;
    const productTitle = button.dataset.productTitle;
    const productUrl = button.dataset.productUrl || window.location.pathname;

    const buttonOptions = this.getFilterOptionsFromButton(button);
    const overrideOptions = Array.isArray(overrides.filterOptions)
      ? overrides.filterOptions
      : [];
    let mergedFilterOptions = this.mergeFilterOptions(
      overrideOptions,
      buttonOptions
    );

    let filterChoice = this.normalizeFilterChoice(overrides.filterChoice);
    let filterChoiceDisplay =
      overrides.filterChoiceDisplay || overrides.filterChoice || null;

    if (!filterChoice && mergedFilterOptions.length > 0) {
      filterChoiceDisplay = mergedFilterOptions[0];
      filterChoice = this.normalizeFilterChoice(filterChoiceDisplay);
    }

    if (
      filterChoice &&
      !mergedFilterOptions.some(
        (option) => this.normalizeFilterChoice(option) === filterChoice
      )
    ) {
      mergedFilterOptions.push(filterChoiceDisplay || filterChoice);
    }

    const availability = this.getProductAvailability(button);

    let existingItem = this.findCartItem(productId, filterChoice);

    let pickupDate;
    if (!availability.isEveryDay) {
      pickupDate = this.getNextAvailablePickupDate(availability);
    } else {
      pickupDate = getMinPickupDate();
    }

    if (existingItem) {
      existingItem.quantity += 1;
      existingItem.filterOptions = this.mergeFilterOptions(
        existingItem.filterOptions,
        mergedFilterOptions
      );
      if (filterChoice) {
        existingItem.filterChoice = filterChoice;
      }
      if (!existingItem.pickupDate) {
        existingItem.pickupDate = pickupDate.toISOString();
      } else {
        const d = new Date(existingItem.pickupDate);
        if (d < new Date()) {
          existingItem.pickupDate = pickupDate.toISOString();
        }
      }
    } else {
      const newItem = {
        productId,
        title: productTitle,
        quantity: 1,
        isEveryDay: availability.isEveryDay,
        days: availability.days,
        pickupDate: pickupDate.toISOString(),
        url: productUrl,
        filterChoice,
        filterOptions: mergedFilterOptions,
      };
      this.cart.items.push(newItem);
      existingItem = newItem;
    }

    const effectiveOptions = existingItem?.filterOptions || mergedFilterOptions;
    const displayChoice = this.getDisplayChoice(effectiveOptions, filterChoice);

    this.calculateSubtotal();
    this.saveCart();
    this.updateCartCount();
    this.renderCartContents();
    const notice = displayChoice
      ? `Toegevoegd: ${productTitle} (${displayChoice})`
      : `Toegevoegd: ${productTitle}`;
    this.showNotification(notice);
    this.renderOrderSide();
  }

  normalizeFilterChoice(value) {
    if (!value) return null;
    const trimmed = value.toString().trim();
    if (
      !trimmed ||
      trimmed.toLowerCase() === "null" ||
      trimmed.toLowerCase() === "undefined"
    ) {
      return null;
    }
    return trimmed;
  }

  escapeHtml(value) {
    if (value === null || value === undefined) return "";
    return value
      .toString()
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  escapeAttribute(value) {
    if (value === null || value === undefined) return "";
    return value
      .toString()
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  mergeFilterOptions(oldOptions = [], newOptions = []) {
    const map = new Map();
    const addOptions = (options) => {
      if (!Array.isArray(options)) return;
      options.forEach((option) => {
        const normalized = this.normalizeFilterChoice(option);
        if (!normalized) return;
        if (!map.has(normalized)) {
          map.set(normalized, option.toString().trim());
        }
      });
    };
    addOptions(oldOptions);
    addOptions(newOptions);
    return Array.from(map.values());
  }

  getDisplayChoice(options = [], choice = null) {
    const normalizedChoice = this.normalizeFilterChoice(choice);
    if (!normalizedChoice) return null;
    if (!Array.isArray(options)) {
      return normalizedChoice;
    }
    const match = options.find(
      (option) => this.normalizeFilterChoice(option) === normalizedChoice
    );
    return match || normalizedChoice;
  }

  getVariantControlMarkup(item, context = "modal") {
    const options = Array.isArray(item.filterOptions) ? item.filterOptions : [];
    if (!options.length) {
      return "";
    }

    const normalizedCurrent =
      this.normalizeFilterChoice(item.filterChoice) ||
      this.normalizeFilterChoice(options[0]);
    const productIdAttr = this.escapeAttribute(item.productId);
    const currentChoiceAttr = this.escapeAttribute(normalizedCurrent);
    const labelClass = "text-xs uppercase tracking-widest text-main/60";
    const selectClass =
      context === "order"
        ? "pr-3 py-1 rounded-md focus:outline-none"
        : "pr-3 py-1 rounded-md focus:outline-none";

    const optionsMarkup = options
      .map((option) => {
        const normalizedOption = this.normalizeFilterChoice(option);
        const valueAttr = this.escapeAttribute(normalizedOption || option);
        const displayAttr = this.escapeAttribute(option);
        const label = this.escapeHtml(option);
        const selected =
          normalizedOption === normalizedCurrent ? " selected" : "";
        return `<option value="${valueAttr}" data-display="${displayAttr}"${selected}>${label}</option>`;
      })
      .join("");

    return `
      <div class="mt-1 cursor-pointer! flex flex-col items-start gap-1 text-sm text-black">
        <select class="${selectClass}" data-variant-selector data-product-id="${productIdAttr}" data-current-choice="${currentChoiceAttr}">
          ${optionsMarkup}
        </select>
      </div>
    `;
  }

  addVariantSelectListeners(root) {
    if (!root) return;
    root.querySelectorAll("[data-variant-selector]").forEach((select) => {
      if (select.dataset.variantBound === "true") return;
      select.addEventListener("change", (event) => {
        const productId = select.dataset.productId;
        const oldChoice = select.dataset.currentChoice || null;
        const selectedOption = select.selectedOptions[0];
        const newChoice = selectedOption ? selectedOption.value : select.value;
        const displayValue = selectedOption
          ? selectedOption.dataset.display || selectedOption.textContent
          : newChoice;
        this.updateItemVariant(productId, oldChoice, newChoice, displayValue);
      });
      select.dataset.variantBound = "true";
    });
  }

  updateItemVariant(productId, oldChoice, newChoice, displayValue = null) {
    const normalizedOld = this.normalizeFilterChoice(oldChoice);
    const normalizedNew = this.normalizeFilterChoice(newChoice);

    if (normalizedNew === null) {
      return;
    }

    const currentItem = this.findCartItem(productId, normalizedOld);
    if (!currentItem) {
      return;
    }

    if (normalizedOld === normalizedNew) {
      return;
    }

    const displayChoice = displayValue || newChoice;

    const mergedOptions = this.mergeFilterOptions(currentItem.filterOptions, [
      displayChoice,
    ]);

    const targetItem = this.findCartItem(productId, normalizedNew);

    if (targetItem) {
      targetItem.quantity += currentItem.quantity;
      targetItem.filterOptions = this.mergeFilterOptions(
        targetItem.filterOptions,
        mergedOptions
      );
      const removeIndex = this.findCartItemIndex(productId, normalizedOld);
      if (removeIndex !== -1) {
        this.cart.items.splice(removeIndex, 1);
      }
    } else {
      currentItem.filterChoice = normalizedNew;
      currentItem.filterOptions = this.mergeFilterOptions(
        currentItem.filterOptions,
        mergedOptions
      );
    }

    this.calculateSubtotal();
    this.saveCart();
    this.updateCartCount();
    this.renderCartContents();
    this.renderOrderSide();
  }

  findCartItem(productId, filterChoice = null) {
    const normalizedChoice = this.normalizeFilterChoice(filterChoice);
    return this.cart.items.find((item) => {
      return (
        item.productId === productId &&
        this.normalizeFilterChoice(item.filterChoice) === normalizedChoice
      );
    });
  }

  findCartItemIndex(productId, filterChoice = null) {
    const normalizedChoice = this.normalizeFilterChoice(filterChoice);
    return this.cart.items.findIndex((item) => {
      return (
        item.productId === productId &&
        this.normalizeFilterChoice(item.filterChoice) === normalizedChoice
      );
    });
  }

  updateItemQuantity(productId, newQuantity, filterChoice = null) {
    // Ensure quantity is a valid number and at least 1
    newQuantity = Math.max(1, parseInt(newQuantity) || 1);
    const item = this.findCartItem(productId, filterChoice);
    if (item) {
      item.quantity = newQuantity;
      this.calculateSubtotal();
      this.saveCart();
      this.updateCartCount();
      this.renderCartContents();
      this.renderOrderSide();
    }
  }

  removeFromCart(productId, filterChoice = null) {
    const itemIndex = this.findCartItemIndex(productId, filterChoice);
    if (itemIndex !== -1) {
      this.cart.items.splice(itemIndex, 1);
      this.calculateSubtotal();
      this.saveCart();
      this.updateCartCount();
      this.renderCartContents();
      this.renderOrderSide();
      this.showNotification("Product verwijdert uit bestelling");
    }
  }

  calculateSubtotal() {
    // No prices in this order form (pickup request, not a payment), so item.price
    // is absent — coerce to 0 so the subtotal is a clean "0.00", never "NaN".
    this.cart.subtotal = this.cart.items
      .reduce(
        (total, item) =>
          total + (Number(item.price) || 0) * (Number(item.quantity) || 0),
        0
      )
      .toFixed(2);
  }

  delLocalStorage() {
    localStorage.clear();
    this.cart = { items: [], subtotal: 0 }; // Reset cart object
    this.updateCartCount();
    this.renderCartContents();
    this.renderOrderSide();
    this.showNotification("Bestelling geleegd");
  }

  saveCart() {
    localStorage.setItem("cart", JSON.stringify(this.cart));
  }

  updateCartCount() {
    const cartToggle = document.querySelector(".hide-toggle");
    if (!cartToggle) return;
    const itemCount = this.cart.items.reduce(
      (total, item) => total + item.quantity,
      0
    );
    // Only include the span if there are items in the cart
    if (itemCount > 0) {
      cartToggle.innerHTML = `
      <span class="relative">
      Bestelling <span class="count w-4 h-4 flex items-center justify-center absolute bg-main text-white -top-2 -right-1 translate-x-full text-xs font-medium">${itemCount}</span></span>
      `;
    } else {
      cartToggle.innerHTML = `Bestelling`;
    }
  }

  getAvailabilityMessages() {
    const itemsWithSpecificAvailability = this.cart.items.filter((item) => {
      return !item.isEveryDay && item.pickupDate;
    });

    if (itemsWithSpecificAvailability.length === 0) {
      return [];
    }

    const groupedByDate = new Map();

    itemsWithSpecificAvailability.forEach((item) => {
      const pickupDate = new Date(item.pickupDate);
      if (Number.isNaN(pickupDate.getTime())) {
        return;
      }

      const dateKey = pickupDate.toISOString().split("T")[0];
      if (!groupedByDate.has(dateKey)) {
        groupedByDate.set(dateKey, {
          date: pickupDate,
          titles: [],
        });
      }

      groupedByDate.get(dateKey).titles.push(item.title);
    });

    const formatter = new Intl.ListFormat("nl-NL", {
      style: "long",
      type: "conjunction",
    });

    return Array.from(groupedByDate.values())
      .sort((a, b) => a.date - b.date)
      .map(({ date, titles }) => {
        const formattedDate = this.formatDate(date);
        const formattedTitles = titles.map(
          (title) => `<strong>${title}</strong>`
        );
        const verb = titles.length > 1 ? "kunnen" : "kan";
        const subjects =
          titles.length > 1
            ? formatter.format(formattedTitles)
            : formattedTitles[0];

        return `${subjects} ${verb} ten vroegste op ${formattedDate} opgehaald worden.`;
      });
  }

  renderCartContents() {
    const modal = document.getElementById("cart-modal");
    if (!modal) return;
    const modalContent = modal.querySelector(".modal-content");
    if (!modalContent) return;
    // If cart is empty
    if (this.cart.items.length === 0) {
      modalContent.innerHTML = `
      <p class="text-center py-8 text-gray-500">Uw winkelmandje is leeg.</p>
      `;
      return;
    }

    // Get availability messages for products with specific availability
    const availabilityMessages = this.getAvailabilityMessages();

    // Start building cart HTML
    let cartItemsHTML = "";

    // Add availability messages if there are any
    if (availabilityMessages.length > 0) {
      cartItemsHTML += `<div class="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4 text-pretty">`;

      availabilityMessages.forEach((message) => {
        cartItemsHTML += `<p class="text-sm text-amber-700 font-medium mb-2">${message}</p>`;
      });

      cartItemsHTML += `</div>`;
    }

    // Add cart items
    cartItemsHTML += `
      <ul class="cart-items space-y-4 mt-0 mb-8">
    `;

    this.cart.items.forEach((item) => {
      const productIdAttr = this.escapeAttribute(item.productId);
      const productUrlAttr = this.escapeAttribute(item.url || "#");
      const normalizedChoice = this.normalizeFilterChoice(item.filterChoice);
      const filterChoiceAttr = this.escapeAttribute(normalizedChoice);
      const variantControl = this.getVariantControlMarkup(item, "modal");
      const availableDaysText = Array.isArray(item.days)
        ? item.days.map((day) => this.escapeHtml(day)).join(", ")
        : "";
      const availableDaysHTML =
        !item.isEveryDay && item.days?.length
          ? `<div class="text-amber-800 bg-amber-100 rounded px-2 py-1 w-fit text-xs mt-2">
              <span class="font-medium">${this.escapeHtml(item.title)}</span> alleen beschikbaar op:<br>
              <span class="font-medium">${availableDaysText}</span>
            </div>`
          : "";

      cartItemsHTML += `
      <li class="cart-item pb-4 last:border-0 hover:letter-spacing-normal!" data-product-id="${productIdAttr}" data-filter-choice="${filterChoiceAttr}">
        <div class="flex justify-between items-start gap-4 w-full">
          <div class="flex-1 text-left">
            <a href="${productUrlAttr}" class="block font-medium text-main! leading-tight hover:text-main transition">
              ${this.escapeHtml(item.title)}
            </a>
            ${variantControl}
          </div>
          <div class="quantity-control flex items-center gap-2">
            <button class="decrease-quantity p-1 w-6 h-6 flex items-center text-main! justify-center" data-product-id="${productIdAttr}" data-filter-choice="${filterChoiceAttr}">-</button>
            <input
              type="text"
              class="item-quantity w-12 mx-2 text-center rounded text-main!"
              value="${item.quantity}"
              min="1"
              data-product-id="${productIdAttr}"
              data-filter-choice="${filterChoiceAttr}"
            >
            <button class="increase-quantity p-1 w-6 h-6  text-main! flex items-center justify-center" data-product-id="${productIdAttr}" data-filter-choice="${filterChoiceAttr}">+</button>
            <button class="remove-item p-2 transition-colors" data-product-id="${productIdAttr}" data-filter-choice="${filterChoiceAttr}">
              <img src="/assets/icons/delete.svg" alt="Verwijder product uit bestelling">
            </button>
          </div>
        </div>
        ${availableDaysHTML}
      </li>
      `;
    });

    cartItemsHTML += `
      </ul>
      <a href="/bestelformulier"  class="btn w-full block text-center mt-6 py-3 px-4 bg-main text-white hover:bg-main/80 transition-all">
        Naar Bestelformulier
      </a>
    `;

    modalContent.innerHTML = cartItemsHTML;

    // Add event listeners to quantity controls
    this.addQuantityControlListeners(modalContent);
  }

  addQuantityControlListeners(modalContent) {
    // Prevent default link behavior when clicking on quantity controls
    modalContent
      .querySelectorAll(".quantity-control button, .item-quantity")
      .forEach((element) => {
        element.addEventListener("click", (e) => {
          e.preventDefault(); // Prevent the link from navigating
          e.stopPropagation(); // Stop event from bubbling up to the link
        });
      });

    // Decrease quantity buttons
    modalContent.querySelectorAll(".decrease-quantity").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault(); // Prevent the link from navigating
        const productId = e.currentTarget.dataset.productId;
        const filterChoice = e.currentTarget.dataset.filterChoice || null;
        const item = this.findCartItem(productId, filterChoice);
        if (item && item.quantity > 1) {
          this.updateItemQuantity(productId, item.quantity - 1, filterChoice);
        }
      });
    });

    // Increase quantity buttons
    modalContent.querySelectorAll(".increase-quantity").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault(); // Prevent the link from navigating
        const productId = e.currentTarget.dataset.productId;
        const filterChoice = e.currentTarget.dataset.filterChoice || null;
        const item = this.findCartItem(productId, filterChoice);
        if (item) {
          this.updateItemQuantity(productId, item.quantity + 1, filterChoice);
        }
      });
    });

    // Quantity input fields
    modalContent.querySelectorAll(".item-quantity").forEach((input) => {
      // Update on blur (when user clicks away)
      input.addEventListener("blur", (e) => {
        e.preventDefault(); // Prevent the link from navigating
        const productId = e.currentTarget.dataset.productId;
        const filterChoice = e.currentTarget.dataset.filterChoice || null;
        this.updateItemQuantity(productId, e.currentTarget.value, filterChoice);
      });

      // Update on Enter key press
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault(); // Prevent the link from navigating
          const productId = e.currentTarget.dataset.productId;
          const filterChoice = e.currentTarget.dataset.filterChoice || null;
          this.updateItemQuantity(
            productId,
            e.currentTarget.value,
            filterChoice
          );
          e.currentTarget.blur(); // Remove focus from input
        }
      });
    });

    // Remove item buttons
    modalContent.querySelectorAll(".remove-item").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault(); // Prevent the link from navigating
        e.stopPropagation(); // Stop event from bubbling up to the link
        const productId = e.currentTarget.dataset.productId;
        const filterChoice = e.currentTarget.dataset.filterChoice || null;
        this.removeFromCart(productId, filterChoice);
      });
    });

    this.addVariantSelectListeners(modalContent);
  }
  toggleModal() {
    const modal = document.querySelector("#cart-modal");
    const cartToggle = document.querySelector(".hide-toggle");
    if (!modal) return;
    if (modal.classList.contains("hidden")) {
      modal.classList.remove("hidden");
      cartToggle?.setAttribute("aria-expanded", "true");
    } else {
      modal.classList.add("hidden");
      cartToggle?.setAttribute("aria-expanded", "false");
    }
  }

  showNotification(message) {
    console.log(message);
    // You can implement a more visual notification here
    const notification = document.createElement("div");
    notification.className =
      "fixed top-4 right-4 bg-main text-white py-2 px-4 rounded-lg shadow-lg z-50";
    notification.textContent = message;
    document.body.appendChild(notification);
    // Remove notification after 3 seconds
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
  // Add this method to your ShoppingCart class
  closeCart() {
    const modal = document.querySelector("#cart-modal");
    const cartToggle = document.querySelector(".hide-toggle");
    if (modal && !modal.classList.contains("hidden")) {
      modal.classList.add("hidden");
      cartToggle?.setAttribute("aria-expanded", "false");
    }
  }
  renderOrderSide() {
    const orderSide = document.getElementById("order-side");
    if (!orderSide) return;

    if (this.cart.items.length === 0) {
      orderSide.innerHTML = `<p class="text-gray-500">Uw bestelling is leeg.</p>`;
      updateOrderTextarea(); // Clear textarea when cart is empty
      return;
    }

    let html = `<ul class="space-y-4">`;
    this.cart.items.forEach((item) => {
      const productIdAttr = this.escapeAttribute(item.productId);
      const normalizedChoice = this.normalizeFilterChoice(item.filterChoice);
      const filterChoiceAttr = this.escapeAttribute(normalizedChoice);
      const variantControl = this.getVariantControlMarkup(item, "order");
      const availableDaysText = Array.isArray(item.days)
        ? item.days.map((day) => this.escapeHtml(day)).join(", ")
        : "";
      const availableDaysHTML =
        !item.isEveryDay && item.days?.length
          ? `<div class="text-amber-800 bg-amber-100 rounded px-2 py-1 w-fit text-xs mt-2">
               <span class="font-medium">${this.escapeHtml(item.title)}</span> alleen beschikbaar op:<br>
               <span class="font-medium">${availableDaysText}</span>
             </div>`
          : "";

      html += `
        <li class="flex flex-col gap-2 font-medium" data-product-id="${productIdAttr}" data-filter-choice="${filterChoiceAttr}">
          <div class="flex justify-between items-start w-full">
            <div class="flex flex-col gap-1 text-left">
              <span>${this.escapeHtml(item.title)}</span>
              ${variantControl}
            </div>
            <div class="flex items-center gap-2">
              <button class="decrease-quantity" data-product-id="${productIdAttr}" data-filter-choice="${filterChoiceAttr}">−</button>
              <input type="text" class="item-quantity w-12 text-center" value="${item.quantity}" data-product-id="${productIdAttr}" data-filter-choice="${filterChoiceAttr}" />
              <button class="increase-quantity" data-product-id="${productIdAttr}" data-filter-choice="${filterChoiceAttr}">+</button>
              <button class="remove-item p-2 transition-colors" data-product-id="${productIdAttr}" data-filter-choice="${filterChoiceAttr}">
                <img src="/assets/icons/delete.svg" alt="Verwijder product uit bestelling">
              </button>
            </div>
          </div>
          ${availableDaysHTML}
        </li>
      `;
    });

    html += `</ul>`;

    orderSide.innerHTML = html;

    this.addQuantityControlListeners(orderSide);

    // Update the bestelling textarea whenever the order side is rendered
    updateOrderTextarea();
  }
  getMinimumPickupDateAndAllowedDays() {
    if (this.cart.items.length === 0) {
      return { date: null, allowedDays: [] };
    }

    let latestDate = null;
    const allowedDaySet = new Set();

    this.cart.items.forEach((item) => {
      // Determine the latest pickup date required
      if (item.pickupDate) {
        const date = new Date(item.pickupDate);
        if (!latestDate || date > latestDate) {
          latestDate = date;
        }
      }

      // Collect allowed days (if item is restricted)
      if (!item.isEveryDay && Array.isArray(item.days)) {
        item.days.forEach((day) => allowedDaySet.add(day));
      }
    });

    // Fallback to 2 days from now
    if (!latestDate) {
      latestDate = new Date();
      latestDate.setDate(latestDate.getDate() + 2);
    }

    return {
      date: latestDate,
      allowedDays: [...allowedDaySet], // Convert Set to Array
    };
  }
  getOverlappingAllowedDays() {
    const allItemDays = this.cart.items
      .filter((item) => !item.isEveryDay)
      .map((item) => item.days);

    if (allItemDays.length === 0) return [];

    return allItemDays.reduce((overlap, currentDays) => {
      return overlap.filter((day) => currentDays.includes(day));
    });
  }
  clearCart() {
    // Empty the in-memory cart
    this.cart = { items: [], subtotal: 0 };
    // Remove only our cart key from localStorage
    localStorage.removeItem("cart");
    // Re-render UI
    this.updateCartCount();
    this.renderCartContents();
    this.renderOrderSide();
    // (optional) toast
    this.showNotification("Bestelling geleegd");
  }
}

document.addEventListener("DOMContentLoaded", () => {
  shoppingCart = new ShoppingCart();

  document.addEventListener("htmx:afterSwap", (evt) => {
    if (!shoppingCart) return;
    const target = evt.target || document;
    shoppingCart.bindAddToCartButtons(target);
  });

  // Attach logic to Freeform navigation buttons
  document
    .querySelectorAll(".freeform-button-next, .freeform-button-submit")
    .forEach((button) => {
      button.addEventListener("click", () => {
        setTimeout(() => {
          renderPickupInputs();
        }, 1000);
      });
    });

  // Populate the order textarea on initial page load (if it exists)
  updateOrderTextarea();

  // Add form submit listener to ensure bestelling is always populated
  const freeform = document.querySelector("form[data-freeform]");
  if (freeform) {
    freeform.addEventListener(
      "submit",
      (e) => {
        updateOrderTextarea();
        if (!validatePickupDatesBeforeSubmit()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      { capture: true } // run before Freeform's own submit/AJAX handler
    );
  }

  // Also hook into submit button click (Freeform may use AJAX before submit event fires)
  const submitButton = document.querySelector(".freeform-button-submit");
  if (submitButton) {
    submitButton.addEventListener(
      "click",
      (e) => {
        updateOrderTextarea();
        if (!validatePickupDatesBeforeSubmit()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      { capture: true }
    ); // capture: true ensures this runs first
  }

  if (isThankYouOrderPage()) {
    shoppingCart.clearCart(true);
  }
});

document.addEventListener("change", function (e) {
  if (e.target && e.target.name === "ophaaltype") {
    renderPickupInputs();
  }
});

// ---------------------------------------------------
// UTILITIES
// ---------------------------------------------------
function parseTimeString(str) {
  const [hh, mm] = str.split(":").map((v) => v.padStart(2, "0"));
  return `${hh}:${mm}`;
}

function getOpeningHoursMap() {
  const raw = document.getElementById("opening-hours-data");
  if (!raw) return {};
  let data;
  try {
    data = JSON.parse(raw.dataset.openingHours);
  } catch (e) {
    console.error("Could not parse opening hours JSON:", e);
    return {};
  }
  const dayMap = { Zon: 0, Maa: 1, Din: 2, Woe: 3, Do: 4, Vrij: 5, Zat: 6 };
  const hoursMap = {};
  data.forEach((entry) => {
    const index = dayMap[entry.dag];
    if (index !== undefined) {
      const ranges = [];
      if (entry.vm) ranges.push(entry.vm);
      if (entry.nm) ranges.push(entry.nm);
      hoursMap[index] = ranges;
    }
  });
  return hoursMap;
}

function getClosedDaysFromOpeningHours() {
  const hoursMap = getOpeningHoursMap();
  // Closed = any dayIndex with no opening ranges
  return Object.keys({ 0: 1, 1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1 })
    .map(Number)
    .filter((idx) => !(idx in hoursMap) || hoursMap[idx].length === 0);
}

function getLeaveRange() {
  const leaveDiv = document.getElementById("annual-leave");
  if (!leaveDiv || !leaveDiv.dataset.annualLeave) return [];

  const leaveData = leaveDiv.dataset.annualLeave.trim();
  if (!leaveData) return [];

  // Split by comma to handle multiple date ranges
  const ranges = leaveData.split(',').map(range => range.trim()).filter(range => range.length > 0);
  const leaveRanges = [];

  ranges.forEach(range => {
    // Try to match format with year: dd/mm/yyyy – dd/mm/yyyy
    let match = range.match(
      /(\d{2})\/(\d{2})\/(\d{4})\s*[–-]\s*(\d{2})\/(\d{2})\/(\d{4})/
    );

    if (match) {
      const [, startDay, startMonth, startYear, endDay, endMonth, endYear] = match;
      const startDate = new Date(`${startYear}-${startMonth}-${startDay}`);
      const endDate = new Date(`${endYear}-${endMonth}-${endDay}`);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      leaveRanges.push([startDate, endDate]);
      return;
    }

    // Fallback: try to match format without year: dd/mm – dd/mm
    match = range.match(
      /(\d{2})\/(\d{2})\s*[–-]\s*(\d{2})\/(\d{2})/
    );

    if (match) {
      const [, startDay, startMonth, endDay, endMonth] = match;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentYear = today.getFullYear();

      // Parse the dates for current year
      let startDate = new Date(`${currentYear}-${startMonth}-${startDay}`);
      let endDate = new Date(`${currentYear}-${endMonth}-${endDay}`);

      // If end date is before start date, it means we're crossing year boundary
      // For example: 22/12 - 03/01 means Dec 22 this year to Jan 3 next year
      if (endDate < startDate) {
        endDate = new Date(`${currentYear + 1}-${endMonth}-${endDay}`);
      }

      // If the end date has already passed, move both dates to next year
      if (endDate < today) {
        startDate = new Date(`${currentYear + 1}-${startMonth}-${startDay}`);
        endDate = new Date(`${currentYear + 1}-${endMonth}-${endDay}`);

        // Check again for year boundary crossing
        if (endDate < startDate) {
          endDate = new Date(`${currentYear + 2}-${endMonth}-${endDay}`);
        }
      }

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      leaveRanges.push([startDate, endDate]);
    }
  });

  console.log('Parsed leave ranges:', leaveRanges.map(range =>
    `${range[0].toLocaleDateString('nl-NL')} - ${range[1].toLocaleDateString('nl-NL')}`
  ));

  return leaveRanges;
}

// ---------------------------------------------------
// GENERIC PICKUP LOGIC FOR DATE + TIME
// ---------------------------------------------------
function setupPickupDateAndTimeFields({
  dateInput,
  timeInput,
  allowedDays,
  minDate, // now always a Date object
  leaveRange = [],
  productTitle = "",
  errorBox = null,
}) {
  if (!dateInput || !timeInput) return;

  // Compose allowedDays as [0...6] (integers)
  const dayNameToIndex = {
    zondag: 0,
    maandag: 1,
    dinsdag: 2,
    woensdag: 3,
    donderdag: 4,
    vrijdag: 5,
    zaterdag: 6,
  };
  const allowedDayIndexes = allowedDays.map((d) =>
    typeof d === "number" ? d : dayNameToIndex[d]
  );

  // Closed days (from opening hours data)
  const closedDays = getClosedDaysFromOpeningHours();

  flatpickr(dateInput, {
    dateFormat: "d-m-Y",
    locale: "nl",
    disableMobile: true,
    // minDate: minDate, // still sets visually, but see disable below
    firstDayOfWeek: 1, // Monday
    disable: [
      function (date) {
        // Normalize the date for comparison (strip time)
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);

        // Block all days before minDate
        if (minDate && checkDate < minDate) return true;

        // Only allow allowed days (as before)
        const day = date.getDay();
        let isAllowed = allowedDayIndexes.length
          ? allowedDayIndexes.includes(day)
          : true;
        let isClosed = closedDays.includes(day);

        // Handle multiple leave periods
        let isDuringLeave = false;
        if (Array.isArray(leaveRange) && leaveRange.length > 0) {
          // Check if date falls within any of the leave ranges
          isDuringLeave = leaveRange.some(range => {
            if (Array.isArray(range) && range.length === 2) {
              const [leaveStart, leaveEnd] = range;
              return checkDate >= leaveStart && checkDate <= leaveEnd;
            }
            return false;
          });
        }
        return !isAllowed || isClosed || isDuringLeave;
      },
    ],
    onChange: function () {
      updateTimeInputForDate({
        dateInput,
        timeInput,
        errorBox,
        productTitle,
      });
    },
  });

  dateInput.addEventListener("change", function () {
    updateTimeInputForDate({
      dateInput,
      timeInput,
      errorBox,
      productTitle,
    });
  });

  // Initialize timeslot on load
  updateTimeInputForDate({
    dateInput,
    timeInput,
    errorBox,
    productTitle,
  });
}

function updateTimeInputForDate({
  dateInput,
  timeInput,
  errorBox = null,
  productTitle = "",
}) {
  const raw = document.getElementById("opening-hours-data");
  if (!dateInput || !timeInput || !dateInput.value || !raw) return;
  // Visually disable the time input while setting up
  timeInput.style.pointerEvents = "none";
  timeInput.style.opacity = "0.6";

  let data;
  try {
    data = JSON.parse(raw.dataset.openingHours);
  } catch (e) {
    console.error("Could not parse opening hours JSON:", e);
    return;
  }

  const dayMap = {
    Zon: 0,
    Maa: 1,
    Din: 2,
    Woe: 3,
    Do: 4,
    Vrij: 5,
    Zat: 6,
  };

  const hoursMap = {};
  data.forEach((entry) => {
    const index = dayMap[entry.dag];
    if (index !== undefined) {
      const ranges = [];
      if (entry.vm) ranges.push(entry.vm);
      if (entry.nm) ranges.push(entry.nm);
      hoursMap[index] = ranges;
    }
  });

  const [day, month, year] = dateInput.value.split("-");
  const selectedDate = new Date(`${year}-${month}-${day}`);
  const dayIndex = selectedDate.getDay();
  const ranges = hoursMap[dayIndex] || [];

  const messageBox = errorBox || document.getElementById("pickup-message");

  if (ranges.length === 0) {
    timeInput.value = "";
    timeInput.disabled = true;
    timeInput.placeholder = "Gesloten";
    if (messageBox) {
      messageBox.textContent =
        (productTitle ? `"${productTitle}" ` : "") +
        "We zijn gesloten op deze dag.";
      messageBox.classList.remove("hidden");
    }
    return;
  }

  // Explicitly clear value at start
  timeInput.value = "";
  timeInput.defaultValue = "";
  timeInput.disabled = false;
  timeInput.placeholder = "HH:MM";

  const parsedRanges = ranges.map((range) => {
    const [start, end] = range.split("-");
    return { start: parseTimeString(start), end: parseTimeString(end) };
  });

  const maxRange = parsedRanges.reduce(
    (latest, range) => (range.end > latest ? range.end : latest),
    "00:00"
  );
  const minRange = parsedRanges.reduce(
    (earliest, range) => (range.start < earliest ? range.start : earliest),
    "23:59"
  );

  timeInput.max = maxRange;
  timeInput.min = minRange;

  const enforceValidTime = () => {
    const current = timeInput.value;
    if (!current) return;

    const isValid = parsedRanges.some(
      (range) => current >= range.start && current <= range.end
    );

    if (!isValid) {
      const nextOpen =
        parsedRanges.find((range) => current < range.start)?.start || maxRange;
      const isTooLate = current > maxRange;
      timeInput.value = nextOpen;
      if (messageBox) {
        messageBox.textContent =
          (productTitle ? `"${productTitle}" ` : "") +
          (isTooLate
            ? `We zijn gesloten om ${current}. Tijd is aangepast naar sluitingstijd (${nextOpen}).`
            : `We zijn gesloten om ${current}. Tijd is aangepast naar openingstijd (${nextOpen}).`);
        messageBox.classList.remove("hidden");
        scrollToIfHidden(messageBox);
      }
      updateOrderTextarea();
    } else {
      if (messageBox) {
        messageBox.textContent = "";
        messageBox.classList.add("hidden");
      }
      updateOrderTextarea();
    }
  };

  if (!timeInput.hasListenerAttached) {
    // Remove any possible input/keyup listeners to prevent early triggering
    timeInput.removeEventListener("input", enforceValidTime);
    timeInput.removeEventListener("keyup", enforceValidTime);
    timeInput.addEventListener("change", enforceValidTime);
    timeInput.hasListenerAttached = true;
    // Re-enable the time input after short delay (setup complete)
    setTimeout(() => {
      timeInput.style.pointerEvents = "";
      timeInput.style.opacity = "";
    }, 400);
  }
  // run validation once immediately to catch any prefilled values (e.g. Safari autofill)
  enforceValidTime();
  updateOrderTextarea();
}

// --------------
// RENDERERS
// --------------

function renderMultiPickupFields() {
  const groupBlock = document.querySelector(
    '.freeform-fieldtype-group[data-field-container="pickup"]'
  );
  if (!groupBlock) return;

  groupBlock.querySelectorAll(".freeform-row").forEach((row) => row.remove());

  const leaveRange = getLeaveRange();

  shoppingCart.cart.items.forEach((item) => {
    const dateInputId = `form-input-ophaaldatum-${item.productId}`;
    const timeInputId = `form-input-ophaaltijd-${item.productId}`;
    const rowHTML = `
      <div class="freeform-row" data-product-id="${item.productId}">
        <div class="freeform-column-6 freeform-fieldtype-datetime freeform-column" data-field-container="ophaaldatum" data-field-type="datetime">
          <label class="freeform-label freeform-required" for="${dateInputId}">${item.title}</label>
          <input type="text"
                 id="${dateInputId}"
                 class="freeform-input form-date-time-field form-datepicker flatpickr-input"
                 name="ophaaldatum-${item.productId}"
                 placeholder="DD-MM-YYYY"
                 data-product-id="${item.productId}"
                 autocomplete="off"
                 readonly="readonly"
                 required>
        </div>
        <div class="freeform-column-6 freeform-fieldtype-datetime freeform-column" data-field-container="ophaaltijd" data-field-type="datetime">
          <label class="freeform-label freeform-required" for="${timeInputId}"></label>
          <input type="time"
                 id="${timeInputId}"
                 class="freeform-input form-date-time-field"
                 name="ophaaltijd-${item.productId}"
                 placeholder="HH:MM"
                 data-product-id="${item.productId}"
                 required>
          <div class="pickup-message text-xs text-amber-700 mt-1 hidden"></div>
        </div>
      </div>
    `;
    groupBlock.insertAdjacentHTML("beforeend", rowHTML);

    // Setup the pickers for each product row
    const dateInput = document.getElementById(dateInputId);
    const timeInput = document.getElementById(timeInputId);
    const errorBox = timeInput.parentNode.querySelector(".pickup-message");

    // Allowed days for this product
    const allowedDays = item.isEveryDay ? [0, 1, 2, 3, 4, 5, 6] : item.days;

    // Minimum pickup date is always at least 2 days from now
    let minDateObj;
    if (item.pickupDate) {
      minDateObj = new Date(item.pickupDate);
      minDateObj.setHours(0, 0, 0, 0);
    } else {
      minDateObj = new Date();
      minDateObj.setHours(0, 0, 0, 0);
    }
    const twoDaysFromNow = getMinPickupDate();
    // Always use the LATER of minDateObj or server today+2
    const minDateFinal = minDateObj > twoDaysFromNow ? minDateObj : twoDaysFromNow;

    setupPickupDateAndTimeFields({
      dateInput,
      timeInput,
      allowedDays,
      minDate: minDateFinal,
      leaveRange,
      productTitle: item.title,
      errorBox,
    });
  });
  bindOrderTextareaListeners();
  updateOrderTextarea();
}

function renderPickupInputs() {
  const groupBlock = document.querySelector(
    '.freeform-fieldtype-group[data-field-container="pickup"]'
  );
  if (!groupBlock) return;

  groupBlock.querySelectorAll(".freeform-row").forEach((row) => row.remove());

  const mode = document.querySelector(
    'input[name="ophaaltype"]:checked'
  )?.value;

  if (mode === "single") {
    const singleRowHTML = `
      <div class="freeform-row">
        <div class="freeform-column-6 freeform-fieldtype-datetime freeform-column" data-field-container="ophaaldatum" data-field-type="datetime">
          <label class="freeform-label freeform-required" for="form-input-ophaaldatum">Ophaaldatum</label>
          <input type="text" id="form-input-ophaaldatum" class="freeform-input form-date-time-field form-datepicker flatpickr-input" name="ophaaldatum" placeholder="DD-MM-YYYY" autocomplete="off" readonly="readonly">
        </div>
        <div class="freeform-column-6 freeform-fieldtype-datetime freeform-column" data-field-container="ophaaltijd" data-field-type="datetime">
          <label class="freeform-label freeform-required" for="form-input-ophaaltijd">Ophaaltijd</label>
          <input type="time" id="form-input-ophaaltijd" class="freeform-input form-date-time-field" name="ophaaltijd" placeholder="HH:MM">
        </div>
        <div id="pickup-message" class="pickup-message text-xs text-amber-700 mt-1 hidden"></div>
      </div>
    `;
    groupBlock.insertAdjacentHTML("beforeend", singleRowHTML);

    let leaveRange = getLeaveRange();
    let minDate, allowedDays;
    ({ date: minDate, allowedDays } =
      shoppingCart.getMinimumPickupDateAndAllowedDays());
    allowedDays = shoppingCart.getOverlappingAllowedDays();

    // Minimum pickup date is always at least 2 days from now
    let minDateObj;
    if (minDate) {
      minDateObj = new Date(minDate);
      minDateObj.setHours(0, 0, 0, 0);
    } else {
      minDateObj = new Date();
      minDateObj.setHours(0, 0, 0, 0);
    }
    const twoDaysFromNow = getMinPickupDate();
    // Always use the LATER of minDateObj or server today+2
    const minDateFinal = minDateObj > twoDaysFromNow ? minDateObj : twoDaysFromNow;

    const hasRestrictedItems = shoppingCart.cart.items.some(
      (item) => !item.isEveryDay
    );

    if (hasRestrictedItems && allowedDays.length === 0) {
      const errorBox = document.getElementById("pickup-message");
      if (errorBox) {
        errorBox.textContent =
          "Geen overlappende dagen beschikbaar voor alle producten. Kies een andere ophaaloptie of verwijder een product.";
        errorBox.classList.remove("hidden");
        scrollToIfHidden(errorBox);
      }
      return;
    }

    setupPickupDateAndTimeFields({
      dateInput: document.querySelector("#form-input-ophaaldatum"),
      timeInput: document.querySelector("#form-input-ophaaltijd"),
      allowedDays,
      minDate: minDateFinal,
      leaveRange,
      errorBox: document.getElementById("pickup-message"),
    });
  } else if (mode === "multi") {
    renderMultiPickupFields();
  }
  bindOrderTextareaListeners();
  updateOrderTextarea();
}

function scrollToIfHidden(element) {
  if (!element) return;
  const rect = element.getBoundingClientRect();
  // If not fully in viewport, scroll
  if (
    rect.top < 0 ||
    rect.bottom > (window.innerHeight || document.documentElement.clientHeight)
  ) {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

function bindOrderTextareaListeners() {
  // Listen to any date/time change
  document
    .querySelectorAll(
      '[id^="form-input-ophaaldatum"],[id^="form-input-ophaaltijd"]'
    )
    .forEach((input) => {
      input.addEventListener("change", updateOrderTextarea);
    });
  // Listen to quantity changes
  document.querySelectorAll(".item-quantity").forEach((input) => {
    input.addEventListener("change", updateOrderTextarea);
    input.addEventListener("blur", updateOrderTextarea);
  });
}

function updateOrderTextarea() {
  const textarea = document.getElementById("form-input-bestelling");
  if (!textarea || !shoppingCart || !shoppingCart.cart) return;

  // If cart is empty, clear the textarea
  if (shoppingCart.cart.items.length === 0) {
    textarea.value = "";
    return;
  }

  const lines = shoppingCart.cart.items.map((item) => {
    // Compose date & time fields
    let pickupDate = "";
    let pickupTime = "";

    // Multi fields
    const dateField = document.querySelector(
      `#form-input-ophaaldatum-${item.productId}`
    );
    const timeField = document.querySelector(
      `#form-input-ophaaltijd-${item.productId}`
    );
    // Single fields fallback
    const singleDate = document.querySelector(`#form-input-ophaaldatum`);
    const singleTime = document.querySelector(`#form-input-ophaaltijd`);

    if (dateField && dateField.value) pickupDate = dateField.value;
    else if (singleDate && singleDate.value) pickupDate = singleDate.value;

    if (timeField && timeField.value) pickupTime = timeField.value;
    else if (singleTime && singleTime.value) pickupTime = singleTime.value;

    const variantDisplay = shoppingCart.getDisplayChoice(
      item.filterOptions,
      item.filterChoice
    );
    const variantLabel = variantDisplay ? ` (${variantDisplay})` : "";

    // Build the line - include pickup info only if available
    let line = `${item.title}${variantLabel} x${item.quantity}`;
    if (pickupDate || pickupTime) {
      line += ` — ophalen op ${pickupDate || "(datum)"} om ${pickupTime || "(tijd)"}`;
    }

    return line;
  });

  textarea.value = lines.filter(Boolean).join("\n");
}

// ADD: detects the thank-you page URL
function isThankYouOrderPage() {
  const p = window.location.pathname.replace(/\/+$/, "").toLowerCase();
  // match /bedankt/bestelling or /bedankt-bestelling
  return p.endsWith("/bedankt/bestelling") || p.endsWith("/bedankt-bestelling");
}
